// Modular entry point that wires classes together
import {
  qs,
  qsa,
  log,
  idFor,
  blockKey,
  exRoundKey,
  countExerciseRoundsDoneFor,
  getTotalRounds,
} from './classes/utils.js';
import { Storage, KEYS } from './classes/storage.js';
import SoundManager from './classes/sound-manager.js';
import ProgressStore from './classes/progress-store.js';
import Timer from './classes/timer.js';
import ProgressUI from './classes/progress-ui.js';
import FeedbackUI from './classes/feedback-ui.js';
import ProgressActions from './classes/progress-actions.js';
import { wireSettingsPanel } from './classes/settings-panel.js';
import { enableViewTransitions } from './classes/view-transitions.js';
import { wireUpdatePrompt } from './classes/update-prompt.js';
import pkg from '../../../package.json' with { type: 'json' };

/** @private Prints a branded version banner to the console, chili-red on cosmic latte. @returns {void} */
function logBrandBanner() {
  // eslint-disable-next-line no-console -- Intentional branded banner, not a diagnostic.
  console.log(
    `%cKOTORI WORKOUT%c ver. ${pkg.version} `,
    'background:oklch(57.74% 0.1852 32.75deg);color:oklch(97.61% 0.022 80.69deg);font-weight:700;letter-spacing:0.04em;padding:4px 10px;border-radius:6px 0 0 6px;',
    'background:oklch(97.61% 0.022 80.69deg);color:oklch(41.68% 0.0231 67.12deg);font-weight:600;padding:4px 10px;border-radius:0 6px 6px 0;'
  );
}
logBrandBanner();

const SOUND = new SoundManager();
SOUND._loadSettings = () =>
  Object.assign({ enabled: true, volume: 1.0, prepBeeps: true }, Storage.getJSON(KEYS.SOUND, {}));
SOUND._saveSettings = () => Storage.setJSON(KEYS.SOUND, SOUND.settings);
const FEEDBACK = new FeedbackUI(document);
FEEDBACK.wireStorageEvents();
const STORE = new ProgressStore();
const UI = new ProgressUI(STORE);
const ACTIONS = new ProgressActions({ store: STORE, ui: UI, feedback: FEEDBACK });
Storage.drainIssues().forEach((issue) => FEEDBACK.showIssue(issue));

/** Bootstraps the current page's storage, controls, timers, and progressive enhancements. @private */
function init() {
  Storage.ensureVersion();
  ACTIONS.wire();
  qsa('.progress-bar[data-week][data-day]').forEach((el) => {
    UI.updateDayProgressBars(el.dataset.week, el.dataset.day);
  });
  // After the bars, because it reads the same day cards they just updated.
  UI.renderResumeCard();

  const meta = qs('#page-meta[data-week][data-day]');
  const dayHeading = qs('#page-title');
  let week = meta?.dataset.week;
  let day = meta?.dataset.day;
  if (!week || !day) {
    const m = dayHeading?.textContent?.match(/Day\s+(\d+)/i);
    const w = location.pathname.match(/\/week\/(\d+)\//);
    if (m && w) {
      day = m[1];
      week = w[1];
    }
  }
  if (week && day) {
    UI.updateDayProgressBars(week, day);
    const blocks = qsa('.exercise-block[data-block-id]');

    blocks.forEach((blockEl) => {
      const blockId = blockEl.getAttribute('data-block-id');
      const totalRounds = getTotalRounds(blockEl);
      const cur = STORE.getCurrentRound(week, day, blockId, totalRounds);

      // Update block/round progress indicators (labels and chips)
      UI.updateBlockRoundsUI(blockEl, cur, totalRounds);

      // Sync exercise and timer button states for the current round
      // This also handles marking everything as done if the block is finished.
      UI.repaintBlockCurrentRound(week, day, blockEl);

      // Handle any rounds that might have been completed but not advanced yet
      UI.advanceBlockRoundIfComplete(week, day, blockEl, null, null, { sideEffects: false });
    });

    STORE.persistExercises();
    UI.wireTimerEvents();
    UI.wireExerciseButtons(week, day);
    Timer.initAll(document, SOUND);
    Timer.initVisibilityPauseOnce();
    Timer.initPageUnloadCleanup();
    wireSettingsPanel(SOUND);
    log('day-init', { week, day, id: idFor(week, day) });
  } else {
    console.warn('[progress] Could not determine week/day on this page');
  }

  markMotionReady();
}

/**
 * @private Opts the page into transitions that should only run for changes the
 * user causes. Everything above paints stored progress into the DOM, and a bar
 * that transitions during that paint animates eight weeks of history on every
 * navigation. Waiting two frames puts the stored values on screen as the
 * starting state rather than as a destination.
 * @returns {void}
 */
function markMotionReady() {
  const enable = () => document.documentElement.classList.add('motion-ready');
  if (typeof requestAnimationFrame !== 'function') {
    enable();
    return;
  }
  requestAnimationFrame(() => requestAnimationFrame(enable));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
  document.addEventListener('DOMContentLoaded', enableViewTransitions, { once: true });
} else {
  init();
  enableViewTransitions();
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => wireUpdatePrompt());
}

/**
 * Browser API for integrations and manual recovery controls.
 * @type {{exportProgress: () => void, importProgress: (file: File) => Promise<void>, resetBlock: (week: number|string, day: number|string, blockId: string) => Promise<void>, resetDay: (week: number|string, day: number|string) => Promise<void>, resetAll: () => Promise<void>}}
 */
window.WorkoutProgress = {
  exportProgress: () => ACTIONS.exportProgress(),
  importProgress: (file) => ACTIONS.importProgress(file),
  resetBlock: (week, day, blockId) => ACTIONS.resetBlock(week, day, blockId),
  resetDay: (week, day) => ACTIONS.resetDay(week, day),
  resetAll: () => ACTIONS.resetAll(),
};

// CommonJS exports for unit tests (if required under Node)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    idFor,
    blockKey,
    exRoundKey,
    countExerciseRoundsDoneFor,
    allExercisesDoneInBlock: ProgressUI.allExercisesDoneInBlock,
    advanceBlockRoundIfComplete: (week, day, blockEl, blockRounds, exCompleted) =>
      new ProgressUI({ blockRounds, exCompleted }).advanceBlockRoundIfComplete(
        week,
        day,
        blockEl,
        blockRounds,
        exCompleted
      ),
  };
}
